import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Button, Alert } from '../components/design-system';
import {
  User, Star, MessageSquare, ChevronRight, Languages, LogOut,
  Bell, Shield, FileText, ThumbsUp, ThumbsDown, Minus, Reply,
} from 'lucide-react';
import courierApi from '../services/courierApi';
import { SUPPORTED_LANGUAGES } from '../i18n/config';

export const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const res = await courierApi.getReviews({ userId: user?.id });
      setReviews(res.reviews || []);
      setReviewStats(res.stats || null);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      await courierApi.replyToReview(reviewId, replyText);
      setReplyingTo(null);
      setReplyText('');
      await loadReviews();
    } catch (err) {
      console.error('Failed to reply:', err);
    } finally {
      setReplyLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getNpsColor = (score) => {
    if (score >= 9) return 'text-green-600';
    if (score >= 7) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-4 pb-2 space-y-3">
      <h1 className="text-lg font-bold text-gray-900 mb-4">{t('nav.profile')}</h1>

      {/* User info */}
      <Card padding="sm">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="h-7 w-7 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-gray-900">
              {user?.firstName} {user?.lastName}
            </h2>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>
      </Card>

      {/* Review Stats */}
      {reviewStats && reviewStats.count > 0 && (
        <Card padding="sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('review.myReviews')}</h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-lg font-bold text-gray-900">{reviewStats.average}</p>
              <p className="text-[10px] text-gray-500">{t('review.stats.average')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className={`text-lg font-bold ${reviewStats.nps >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reviewStats.nps}
              </p>
              <p className="text-[10px] text-gray-500">{t('review.stats.nps')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-lg font-bold text-gray-900">{reviewStats.count}</p>
              <p className="text-[10px] text-gray-500">{t('review.stats.total')}</p>
            </div>
          </div>

          {/* NPS breakdown */}
          <div className="flex items-center gap-2 mt-3 text-xs">
            <div className="flex items-center gap-1 text-green-600">
              <ThumbsUp className="h-3 w-3" />
              <span>{reviewStats.promoters}</span>
            </div>
            <div className="flex items-center gap-1 text-yellow-600">
              <Minus className="h-3 w-3" />
              <span>{reviewStats.passives}</span>
            </div>
            <div className="flex items-center gap-1 text-red-600">
              <ThumbsDown className="h-3 w-3" />
              <span>{reviewStats.detractors}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Reviews list */}
      {reviews.length > 0 && (
        <div className="space-y-2">
          {reviews.map((review) => (
            <Card key={review.id} padding="sm">
              <div className="flex items-start gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  review.npsScore >= 9 ? 'bg-green-500' : review.npsScore >= 7 ? 'bg-yellow-500' : 'bg-red-500'
                }`}>
                  {review.npsScore}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={review.reviewerRole === 'customer' ? 'info' : 'primary'} size="sm">
                      {t(`contract.${review.reviewerRole}`)}
                    </Badge>
                    <span className="text-[10px] text-gray-400">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-700 mb-2">{review.comment}</p>
                  )}

                  {/* Reply */}
                  {review.replyText ? (
                    <div className="bg-gray-50 rounded-lg p-2 mt-2">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Reply className="h-3 w-3" />
                        Your reply
                      </p>
                      <p className="text-sm text-gray-700">{review.replyText}</p>
                    </div>
                  ) : review.revieweeUserId === user?.id && (
                    replyingTo === review.id ? (
                      <div className="mt-2">
                        <textarea
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          rows={2}
                          placeholder={t('review.replyPlaceholder')}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                        />
                        <div className="flex gap-2 mt-1">
                          <Button size="sm" onClick={() => handleReply(review.id)} loading={replyLoading}>
                            {t('review.submitReply')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setReplyingTo(review.id)}
                        className="text-xs text-primary-600 font-medium mt-1 flex items-center gap-1"
                      >
                        <Reply className="h-3 w-3" />
                        {t('review.reply')}
                      </button>
                    )
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Language */}
      <Card padding="sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('language.selectLanguage')}</h3>
        <div className="flex gap-2">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => i18n.changeLanguage(lang.code)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                i18n.language === lang.code
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {lang.nativeName}
            </button>
          ))}
        </div>
      </Card>

      {/* Logout */}
      <Button fullWidth variant="danger" onClick={handleLogout}>
        <LogOut className="h-4 w-4" />
        {t('auth.logout')}
      </Button>
    </div>
  );
};
